import React, { useRef } from "react";
import {
  Box,
  Container,
  Typography,
  Card,
  Grid,
  Stack,
  useTheme,
  alpha,
  Button,
  Divider,
  ImageList,
  ImageListItem,
  IconButton,
  Tooltip,
} from "@mui/material";
import {
  Search as SearchIcon,
  Email as EmailIcon,
  ContentCopy as ContentIcon,
  People as SocialIcon,
  Campaign as PPCIcon,
  Stars as InfluencerIcon,
  OpenInNew as OpenInNewIcon,
} from "@mui/icons-material";
import Footer from "../Components/Utils/Footer";
import Hero from "../Components/Landing/Hero";
import Header from "../Components/Utils/Header";
import FAQ from "../Components/Utils/FAQ";

// Import all marketing images
const MARKETING_IMAGES = {
  image1: require("../assets/images/marketing/image1.jpg"),
  image2: require("../assets/images/marketing/image2.jpg"),
  image3: require("../assets/images/marketing/image3.jpg"),
  image4: require("../assets/images/marketing/image4.jpg"),
  image5: require("../assets/images/marketing/image5.jpg"),
  image6: require("../assets/images/marketing/image6.jpg"),
  image7: require("../assets/images/marketing/image7.jpg"),
  image8: require("../assets/images/marketing/image8.jpg"),
  // ... import all your marketing images
};

const SERVICES = [
  {
    icon: SearchIcon,
    title: "SEO Optimization",
    desc: "From keyword research and on-page optimization to link building and content creation, we optimize every aspect of your website to attract organic traffic and increase your online presence.",
    color: "primary",
  },
  {
    icon: PPCIcon,
    title: "Pay-Per-Click Advertising",
    desc: "We design and manage targeted PPC advertising campaigns that drive qualified leads and maximize your advertising budget.",
    color: "secondary",
  },
  {
    icon: SocialIcon,
    title: "Social Media Marketing",
    desc: "Our social media marketers develop customized strategies to enhance your brand presence and engage with your audience across social media platforms.",
    color: "info",
  },
  {
    icon: ContentIcon,
    title: "Content Marketing",
    desc: "Our team of content creators, including writers, designers, and videographers, develops high-quality content that resonates with your target audience.",
    color: "success",
  },
  {
    icon: EmailIcon,
    title: "Email Marketing",
    desc: "We create personalized email marketing campaigns that nurture leads, foster customer loyalty, and drive conversions.",
    color: "warning",
  },
  {
    icon: InfluencerIcon,
    title: "Influencer Marketing",
    desc: "We offer influencer marketing services that help businesses leverage the influence and reach of social media influencers to promote their products or services.",
    color: "error",
  },
];

const PORTFOLIO = [
  {
    img: MARKETING_IMAGES.image1,
    title: "Social Media Campaign",
    description: "Brand awareness campaign for tech startup",
    cols: 1,
  },
  {
    img: MARKETING_IMAGES.image2,
    title: "Email Marketing Campaign",
    description: "Email campaign for SaaS company",
    cols: 1,
  },
  {
    img: MARKETING_IMAGES.image3,
    title: "SEO Campaign",
    description: "SEO campaign for e-commerce site",
    cols: 1,
  },
  {
    img: MARKETING_IMAGES.image4,
    title: "PPC Campaign",
    description: "PPC campaign for local business",
    cols: 1,
  },
  {
    img: MARKETING_IMAGES.image5,
    title: "Influencer Campaign",
    description: "Influencer campaign for fashion brand",
    cols: 1,
  },
  {
    img: MARKETING_IMAGES.image6,
    title: "Content Marketing Campaign",
    description: "Content marketing campaign for tech company",
    cols: 1,
  },
  {
    img: MARKETING_IMAGES.image7,
    title: "Social Media Campaign",
    description: "Social media campaign for travel agency",
    cols: 1,
  },
  {
    img: MARKETING_IMAGES.image8,
    title: "Email Marketing Campaign",
    description: "Email campaign for SaaS company",
    cols: 1,
  },
];

const MARKETING_FAQS = [
  {
    question: "What digital marketing services do you offer?",
    answer:
      "We offer comprehensive digital marketing services including SEO optimization, social media marketing, content marketing, email campaigns, PPC advertising, and influencer marketing. Each service is tailored to meet your specific business goals and target audience.",
  },
  {
    question: "How much does social media management cost?",
    answer:
      "Our standard social media management package is Ksh. 15,000 per month, which includes daily posts across 3 social media platforms (6 days a week), custom graphics, content calendar, and performance monitoring. Custom packages are also available based on your needs.",
  },
  {
    question: "How do you measure marketing success?",
    answer:
      "We track key performance indicators (KPIs) specific to your goals, including website traffic, conversion rates, engagement rates, lead generation, and ROI. We provide detailed monthly reports showing the impact of our marketing efforts and areas for optimization.",
  },
  {
    question: "How long does it take to see results?",
    answer:
      "While some results can be seen immediately (like increased social media engagement), sustainable growth typically takes 3-6 months. SEO efforts usually show significant results within 4-6 months. We provide regular updates and adjust strategies as needed for optimal results.",
  },
  {
    question: "Do you create the content for social media?",
    answer:
      "Yes, our team creates all the content, including custom graphics, captions, and hashtags. We work with you to understand your brand voice and ensure all content aligns with your business objectives. All posts are sent for your approval before publishing.",
  },
  {
    question: "Can you help with marketing strategy?",
    answer:
      "Absolutely! We begin with a comprehensive marketing audit and develop a customized strategy based on your business goals, target audience, and market analysis. This includes competitor analysis, channel selection, content planning, and budget allocation.",
  },
  {
    question: "Do you offer website SEO services?",
    answer:
      "Yes, we provide complete SEO services including keyword research, on-page optimization, technical SEO, content strategy, link building, and local SEO. We also provide monthly reports showing your website's search performance and rankings.",
  },
];

const Marketing = React.forwardRef((props, ref) => {
  const theme = useTheme();
  const marketingRef = useRef(null);

  return (
    <Box sx={{ minHeight: "100vh" }}>
      <Header active="Marketing" />
      <Hero index={2} />

      {/* Services Section */}
      <Box
        ref={ref || marketingRef}
        sx={{
          py: { xs: 8, md: 12 },
          backgroundColor: "background.default",
          position: "relative",
          "&::before": {
            content: '""',
            position: "absolute",
            top: 0,
            left: 0,
            right: 0,
            height: "100%",
            background: (theme) =>
              `linear-gradient(180deg, ${alpha(
                theme.palette.primary.main,
                0.05
              )}, transparent)`,
          },
        }}
      >
        <Container>
          <Stack spacing={6}>
            <Stack spacing={2}>
              <Typography
                variant="h4"
                sx={{
                  fontWeight: 700,
                  color: "text.primary",
                  textAlign: "center",
                }}
                data-aos="fade-up"
              >
                Digital Marketing Services
              </Typography>
              <Typography
                variant="h5"
                sx={{
                  color: "text.secondary",
                  textAlign: "center",
                }}
                data-aos="fade-up"
                data-aos-delay="100"
              >
                Comprehensive digital marketing solutions that drive results and
                help businesses thrive in the online world
              </Typography>
            </Stack>

            <Grid container spacing={3}>
              {SERVICES.map((service, index) => (
                <Grid item xs={12} md={6} key={service.title}>
                  <ServiceCard {...service} index={index} />
                </Grid>
              ))}
            </Grid>
          </Stack>
        </Container>
      </Box>

      {/* Portfolio Section */}
      <Box sx={{ py: { xs: 8, md: 12 }, bgcolor: "background.paper" }}>
        <Container maxWidth="lg">
          <Stack spacing={6}>
            <Typography
              variant="h3"
              sx={{
                textAlign: "center",
                mb: 4,
              }}
              data-aos="fade-up"
            >
              Recent Marketing Projects
            </Typography>

            <ImageList
              variant="quilted"
              cols={4}
              gap={24}
              sx={{ overflow: "hidden" }}
            >
              {PORTFOLIO.map((item, index) => (
                <ImageListItem
                  key={item.title}
                  cols={item.cols || 1}
                  rows={item.rows || 1}
                  data-aos="fade-up"
                  data-aos-delay={index * 100}
                >
                  <Box
                    sx={{
                      position: "relative",
                      height: "100%",
                      overflow: "hidden",
                      borderRadius: 2,
                      "&:hover .overlay": {
                        opacity: 1,
                      },
                      "&:hover img": {
                        transform: "scale(1.1)",
                      },
                    }}
                  >
                    <Box
                      component="img"
                      src={item.img}
                      alt={item.title}
                      sx={{
                        height: "100%",
                        width: "100%",
                        objectFit: "cover",
                        transition: "transform 0.3s ease-in-out",
                      }}
                    />
                    <Box
                      className="overlay"
                      sx={{
                        position: "absolute",
                        top: 0,
                        left: 0,
                        right: 0,
                        bottom: 0,
                        bgcolor: alpha(theme.palette.common.black, 0.7),
                        opacity: 0,
                        transition: "opacity 0.3s ease-in-out",
                        display: "flex",
                        flexDirection: "column",
                        justifyContent: "center",
                        alignItems: "center",
                        p: 2,
                      }}
                    >
                      <Typography
                        variant="h6"
                        sx={{ color: "common.white", mb: 1 }}
                      >
                        {item.title}
                      </Typography>
                      <Typography
                        variant="body2"
                        sx={{ color: "common.white", textAlign: "center" }}
                      >
                        {item.description}
                      </Typography>
                      {/* <Tooltip title="View Project">
                        <IconButton
                          sx={{
                            color: "common.white",
                            mt: 2,
                            "&:hover": {
                              transform: "scale(1.1)",
                            },
                          }}
                        >
                          <OpenInNewIcon />
                        </IconButton>
                      </Tooltip> */}
                    </Box>
                  </Box>
                </ImageListItem>
              ))}
            </ImageList>
          </Stack>
        </Container>
      </Box>

      {/* FAQ Section */}
      <FAQ
        title="Digital Marketing FAQs"
        subtitle="Get answers to common questions about our digital marketing services"
        faqs={MARKETING_FAQS}
      />

      <Footer />
    </Box>
  );
});

function ServiceCard({ icon: Icon, title, desc, color, index }) {
  const theme = useTheme();

  return (
    <Card
      elevation={0}
      data-aos="fade-up"
      data-aos-delay={index * 100}
      sx={{
        height: "100%",
        p: 4,
        transition: "all 0.3s ease-in-out",
        border: `1px solid ${alpha(theme.palette[color].main, 0.1)}`,
        "&:hover": {
          transform: "translateY(-8px)",
          boxShadow: theme.shadows[8],
          borderColor: "transparent",
          bgcolor: (theme) =>
            `linear-gradient(135deg, ${alpha(
              theme.palette[color].main,
              0.05
            )}, ${alpha(theme.palette[color].main, 0.1)})`,
        },
      }}
    >
      <Stack spacing={3}>
        <Box
          sx={{
            width: 56,
            height: 56,
            borderRadius: 2,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            bgcolor: (theme) => alpha(theme.palette[color].main, 0.1),
          }}
        >
          <Icon
            sx={{
              fontSize: 32,
              color: `${color}.main`,
            }}
          />
        </Box>

        <Box>
          <Typography
            variant="h5"
            gutterBottom
            sx={{
              fontWeight: 600,
              color: "text.primary",
            }}
          >
            {title}
          </Typography>
          <Typography variant="body1" color="text.secondary">
            {desc}
          </Typography>
        </Box>

        <Button
          variant="text"
          color={color}
          endIcon={<OpenInNewIcon />}
          sx={{
            alignSelf: "flex-start",
            "&:hover": {
              transform: "translateX(4px)",
            },
          }}
        >
          Learn More
        </Button>
      </Stack>
    </Card>
  );
}

export default Marketing;
